import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    MenuItem,
    Select,
    TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { UpdateTag } from "../../../api/Tag";
import {
    GetTagKindButtonColor,
    TagBank,
    Tag as TagData,
    TagKindItems,
} from "../../../model/TagData";
import { useAppDispatch } from "../../../store/hooks";
import { actionSetTags } from "../../../store/question/action";
import { selectTags } from "../../../store/question/selector";
import { ensureTagBank } from "../../../store/root/ensure";
import { actionUpdateTag } from "../../../store/root/reducer";
import { selectTagBank } from "../../../store/root/selector";
import { selectAdminMode, selectEditMode } from "../../../store/user/selector";
import { Colors } from "../../../tools/color";
import { CheckBox } from "../../basic/inputs/checkbox/check_box";

export function Tags(props: { marginTop?: boolean }) {
    const { marginTop } = props;
    const dispatch = useAppDispatch();

    const tags = useSelector(selectTags);
    return (
        <TagsWithParam
            tags={tags}
            setTags={(value, afterSuccess) => {
                dispatch(
                    actionSetTags(
                        value.map((t) => t.label),
                        afterSuccess
                    )
                );
            }}
            marginTop={marginTop}
        />
    );
}

export function TagsWithParam(props: {
    tags: TagData[];
    setTags: (ts: TagData[], afterSuccess: () => void) => void;
    marginTop?: boolean;
}) {
    const { tags, setTags, marginTop } = props;
    const dispatch = useAppDispatch();

    const tagBank = useSelector(selectTagBank);

    const [edit, setEdit] = useState(false);
    const editMode = useSelector(selectEditMode);

    useEffect(() => {
        ensureTagBank(tagBank, dispatch);
    }, [tagBank.is_backend_data, dispatch, tagBank]);

    return (
        <Box
            sx={{
                display: "flex",
                minWidth: "200px",
                mt: marginTop && tags.length > 0 ? "20px" : 0,
            }}
        >
            {editMode && edit ? (
                <TagsEdit bank={tagBank} tags={tags} setEdit={setEdit} setTags={setTags} />
            ) : (
                <TagsDisplay tags={tags} setEdit={setEdit} />
            )}
        </Box>
    );
}

interface TagsDisplayProps {
    tags: TagData[];
    setEdit: (b: boolean) => void;
}

function TagsDisplay(props: TagsDisplayProps) {
    const editMode = useSelector(selectEditMode);
    const adminMode = useSelector(selectAdminMode);

    const { tags, setEdit } = props;

    return (
        <>
            {tags.map((tag, index) => (
                <div key={index}>{tagCanDisplay(tag, adminMode) && <TagDisplay tag={tag} />}</div>
            ))}
            {editMode && (
                <IconButton
                    color="warning"
                    onClick={() => {
                        setEdit(true);
                    }}
                >
                    <EditIcon />
                </IconButton>
            )}
        </>
    );
}

export function TagDisplay(props: { tag: TagData }) {
    const { tag } = props;
    return (
        <Button
            variant="contained"
            color={GetTagKindButtonColor(tag.kind)}
            sx={{
                height: "30px",
                minWidth: "50px",
                margin: "5px",
                padding: "10px",
            }}
            onClick={() => {
                if (tag.url.length > 0) {
                    window.open(tag.url, "_blank", "noopener,noreferrer");
                }
            }}
        >
            <span style={{ color: tag.hide ? Colors["admin"] : undefined }}>
                <b>
                    {tag.label}
                    {tag.hide && <sup>h</sup>}
                </b>
            </span>
        </Button>
    );
}

interface TagsEditProps {
    bank: TagBank;
    tags: TagData[];
    setTags: (tags: TagData[], afterSuccess: () => void) => void;
    setEdit: (b: boolean) => void;
}

function TagsEdit(props: TagsEditProps) {
    const { tags, bank, setEdit, setTags } = props;
    const [value, setValue] = useState<TagData[]>(tags);

    return (
        <>
            <TagLabelsInput tagsAll={bank.tags} value={value} setValue={setValue} />
            <>
                <IconButton
                    color="info"
                    onClick={() => {
                        setTags(value, () => setEdit(false));
                    }}
                >
                    <SaveIcon />
                </IconButton>
                <IconButton
                    color="warning"
                    onClick={() => {
                        setValue(tags);
                        setEdit(false);
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </>
        </>
    );
}

interface TagLabelsInputProps {
    tagsAll: TagData[];
    value: TagData[];
    setValue: (v: TagData[]) => void;
}

export function TagLabelsInput(props: TagLabelsInputProps) {
    const { tagsAll: tagAll, value, setValue } = props;

    return (
        <Autocomplete
            fullWidth
            size="small"
            multiple
            options={Array.from(tagAll)}
            freeSolo
            value={value}
            onChange={(event: any, newValue: (TagData | string)[]) => {
                const newVal = newValue.map((v) => (typeof v === "string" ? new TagData(v) : v));
                setValue(newVal);
            }}
            renderTags={(value: readonly TagData[], getTagProps) =>
                value.map((option: TagData, index: number) => {
                    return <TagDisplay tag={option} />;
                })
            }
            renderInput={(params) => {
                return (
                    <TextField
                        {...params}
                        InputProps={{
                            ...params.InputProps,
                            type: "search",
                        }}
                    />
                );
            }}
        />
    );
}

function tagCanDisplay(t: TagData, isAdmin: boolean) {
    if (isAdmin) {
        return true;
    }
    if (t.hide) {
        return false;
    }
    return true;
}

interface TagEditDialogProps {
    open: boolean;
    onClose: () => void;
    tag: TagData;
}

export function TagEditDialog(props: TagEditDialogProps) {
    const dispatch = useAppDispatch();

    const { open, onClose, tag } = props;

    const [label, setLabel] = useState("");
    const [url, setUrl] = useState("");
    const [hide, setHide] = useState(false);
    const [kind, setKind] = useState(0);

    useEffect(() => {
        if (open) {
            setLabel(tag.label);
            setUrl(tag.url);
            setHide(tag.hide);
            setKind(tag.kind);
        }
    }, [open]);

    const handleClose = () => {
        onClose();
    };

    const handleSave = () => {
        const newtag = {
            ...tag,
            label,
            url,
            hide,
            kind,
        } as TagData;
        UpdateTag({
            tag: newtag,
            afterSuccess: (rsptag) => {
                dispatch(actionUpdateTag(rsptag));
                onClose();
            },
            dispatch,
        });
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth>
            <DialogTitle>标签编辑</DialogTitle>
            <DialogContent>
                <Box sx={{ mt: "30px" }}>
                    <b>类型</b>
                    <FormControl fullWidth size="small">
                        <Select
                            value={kind}
                            onChange={(e) => {
                                setKind(e.target.value as number);
                            }}
                        >
                            {TagKindItems.map((item, index) => {
                                return (
                                    <MenuItem value={item.val}>
                                        <Button
                                            color={GetTagKindButtonColor(item.val)}
                                            variant="contained"
                                            size="small"
                                        >
                                            {item.name}
                                        </Button>
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={{ marginTop: "30px" }}>
                    <b>名称</b>
                    <TextField
                        sx={{ marginTop: "10px" }}
                        fullWidth
                        variant="standard"
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                    />
                </Box>

                <Box sx={{ marginTop: "30px" }}>
                    <b>属性</b>
                    <CheckBox checked={hide} onClick={(b) => setHide(b)} label="对外不可见" />
                </Box>

                <Box sx={{ marginTop: "30px" }}>
                    <b>跳转地址</b>
                    <TextField
                        sx={{ marginTop: "10px" }}
                        fullWidth
                        variant="standard"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleSave}>保存</Button>
                <Button onClick={handleClose} autoFocus color="info">
                    取消
                </Button>
            </DialogActions>
        </Dialog>
    );
}
